import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "../../styles/Blog.css";
import NotFound from "../NotFound";
import strings from "../../blog/strings";
import useBlogHead from "../../blog/seo";
import { blogLd, breadcrumbLd } from "../../blog/schema";
import {
  getPage,
  getPages,
  getPostsBySlugs,
  getTags,
  langConfig,
  absolute,
} from "../../blog/data";
import { Breadcrumbs, PostCard, Pagination } from "./parts";

function BlogIndex({ lang }) {
  const params = useParams();
  const number = params.page ? Number(params.page) : 1;
  const page = Number.isInteger(number) ? getPage(lang, number) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [number]);

  if (!page) return <NotFound />;

  const t = strings(lang);
  const cfg = langConfig(lang);
  const posts = getPostsBySlugs(lang, page.posts);
  const pages = getPages(lang);
  const tags = getTags(lang);
  const isFirst = page.number === 1;

  // Strony paginacji są indeksowalne i kanoniczne same na siebie — canonical na
  // stronę 1 ukrywałby artykuły z dalszych stron przed crawlerem.
  const title = isFirst ? cfg.title : `${cfg.title} — ${t.pageTitle(page.number)}`;
  const crumbs = isFirst
    ? [{ name: t.home, path: "/" }, { name: cfg.title }]
    : [
        { name: t.home, path: "/" },
        { name: cfg.title, path: cfg.indexPath },
        { name: t.pageTitle(page.number) },
      ];

  const prev = pages.find((p) => p.number === page.number - 1);
  const next = pages.find((p) => p.number === page.number + 1);

  return (
    <div className="blog">
      <BlogIndexHead
        lang={lang}
        cfg={cfg}
        page={page}
        title={title}
        posts={posts}
        crumbs={crumbs}
        prev={prev}
        next={next}
      />

      <div className="blog-shell">
        <Breadcrumbs lang={lang} items={crumbs} />

        <header className="blog-index-header">
          <h1>{isFirst ? cfg.title : title}</h1>
          <p className="blog-lead">{cfg.description}</p>
          <p className="blog-index-feed">
            <a href={cfg.rssPath}>{t.rss}</a>
          </p>
        </header>

        {tags.length > 0 && (
          <nav className="blog-tagcloud" aria-label={t.tags}>
            <ul>
              {tags.map((tag) => (
                <li key={tag.tag}>
                  <Link to={tag.path}>
                    #{tag.tag} <span className="blog-tagcloud-count">{tag.count}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="blog-list">
          {posts.map((post) => (
            <PostCard key={post.slug} lang={lang} post={post} />
          ))}
        </div>

        <Pagination lang={lang} page={page} pages={pages} />
      </div>
    </div>
  );
}

function BlogIndexHead({ lang, cfg, page, title, posts, crumbs, prev, next }) {
  useBlogHead({
    title,
    description: cfg.description,
    canonical: page.url,
    lang,
    locale: cfg.locale,
    ogType: "website",
    rss: cfg.rssUrl,
    prev: prev ? prev.url : undefined,
    next: next ? next.url : undefined,
    jsonLd: [
      blogLd({
        url: cfg.indexUrl,
        name: cfg.title,
        description: cfg.description,
        lang,
        posts,
      }),
      breadcrumbLd(
        crumbs.map((c) => ({ name: c.name, url: c.path ? absolute(c.path) : page.url }))
      ),
    ],
  });
  return null;
}

export default BlogIndex;
